import { Component, OnInit } from '@angular/core';
import { AccountingReportsService } from '../accounting-reports.service';
import { AccountingBalanceSheetActiveModel } from '../../models/accounting-balancesheet-active.model';
import { AccountingBalanceSheetEquityAndLiabilitiesModel } from '../../models/accounting-balancesheet-equityandliabilities.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import * as FileSaver from 'file-saver';
import { Utils } from '../../../../shared/utils';
import { UntypedFormControl, Validators } from '@angular/forms';
import { CoreComponent } from '../../../../core/header/core.component';
import { consts } from '../../../../consts';
import { Option } from '../../../../shared/models/option.model';

@Component({
    selector: 'convfact-accounting-balancesheet',
    templateUrl: './accounting-balancesheet.component.html',
    styleUrls: ['./accounting-balancesheet.component.css'],
    standalone: false
})
export class AccountingBalanceSheetComponent implements OnInit {
  public isLoadingActive: boolean = false;
  public accountingBalanceSheetActiveModel: AccountingBalanceSheetActiveModel;

  public isLoadingEquityAndLiabilities: boolean = false;
  public accountingBalanceSheetEquityAndLiabilitiesModel: AccountingBalanceSheetEquityAndLiabilitiesModel;

  public fromFilterBalance: UntypedFormControl;
  public toFilterBalance: UntypedFormControl;
  public globalFilterBalance: UntypedFormControl;

  public fromToFilterBalanceOptions: Option[] = [
    { label: 'Apertura', value: '0' },
    ...['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
      .map((m, i) => ({ label: m, value: (i + 1).toString() })),
    { label: 'Regularización', value: '13' },
    { label: 'Cierre', value: '14' },
  ];

  constructor(
    private accountingReportsService: AccountingReportsService,
    public snackBar: MatSnackBar,
    public core: CoreComponent
  ) {
    this.accountingBalanceSheetActiveModel = new AccountingBalanceSheetActiveModel();
    this.accountingBalanceSheetEquityAndLiabilitiesModel = new AccountingBalanceSheetEquityAndLiabilitiesModel();
  }

  ngOnInit(): void {
    this.setDefaultValesFormControl();
    this.loadBalanceSheetActive();
    this.loadBalanceSheetEquityAndLiabilities();
  }

  private setDefaultValesFormControl(): void {
    const actualDate: Date = new Date();
    let actualMonth: number = actualDate.getMonth();
    actualMonth++;

    this.fromFilterBalance = new UntypedFormControl('0', [Validators.required]);
    this.toFilterBalance = new UntypedFormControl(actualMonth.toString(), [Validators.required]);

    const activities = this.core.activity;
    if (activities.length == 1) {
      this.globalFilterBalance = new UntypedFormControl(true, [Validators.requiredTrue]);
    } else {
      this.globalFilterBalance = new UntypedFormControl(false, [Validators.requiredTrue]);
    }
  }

  private loadBalanceSheetActive(): void {
    this.isLoadingActive = true;

    this.accountingReportsService
      .getBalanceSheetActiveModel(
        this.globalFilterBalance.value,
        this.fromFilterBalance.value,
        this.toFilterBalance.value
      )
      .then(response => {
        this.accountingBalanceSheetActiveModel = response;
        this.isLoadingActive = false;
      })
      .catch(err => {
        this.displayInfo(err['message']);
        this.isLoadingActive = false;
      });
  }

  public excelBalanceSheetActive(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.isLoadingActive = true;

    const request = {
      global: this.globalFilterBalance.value,
      from: this.fromFilterBalance.value,
      to: this.toFilterBalance.value,
      balanceSheetActiveViewModel: this.accountingBalanceSheetActiveModel
    };

    this.accountingReportsService
      .getExcelBalanceSheetActive(request)
      .then(response => {
        FileSaver.saveAs(response, Utils.GetExcelName('Balance Pymes - Activo'));
        this.isLoadingActive = false;
      })
      .catch(() => {
        this.displayInfo('Ha ocurrido un error inesperado al exportar el documento excel');
        this.isLoadingActive = false;
      });
  }

  public pdfBalanceSheetActive(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.isLoadingActive = true;

    const request = {
      global: this.globalFilterBalance.value,
      from: this.fromFilterBalance.value,
      to: this.toFilterBalance.value,
      balanceSheetActiveViewModel: this.accountingBalanceSheetActiveModel
    };

    this.accountingReportsService
      .getPdfBalanceSheetActive(request)
      .then(response => {
        FileSaver.saveAs(response, Utils.GetPdfName('Balance Pymes - Activo'));
        this.isLoadingActive = false;
      })
      .catch(() => {
        this.displayInfo('Ha ocurrido un error inesperado al exportar el documento PDF');
        this.isLoadingActive = false;
      });
  }

  public filterBalance(): void {
    this.loadBalanceSheetActive();
    this.loadBalanceSheetEquityAndLiabilities();
  }

  private loadBalanceSheetEquityAndLiabilities(): void {
    this.isLoadingEquityAndLiabilities = true;

    this.accountingReportsService
      .getBalanceSheetEquityAndLiabilitiesModel(
        this.globalFilterBalance.value,
        this.fromFilterBalance.value,
        this.toFilterBalance.value
      )
      .then(response => {
        this.accountingBalanceSheetEquityAndLiabilitiesModel = response;
        this.isLoadingEquityAndLiabilities = false;
      })
      .catch(err => {
        this.displayInfo(err['message']);
        this.isLoadingEquityAndLiabilities = false;
      });
  }

  public excelBalanceSheetEquityAndLiabilities(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.isLoadingEquityAndLiabilities = true;

    const request = {
      global: this.globalFilterBalance.value,
      from: this.fromFilterBalance.value,
      to: this.toFilterBalance.value,
      balanceSheetEquityAndLiabilitiesViewModel: this.accountingBalanceSheetEquityAndLiabilitiesModel
    };

    this.accountingReportsService
      .getExcelBalanceSheetEquityAndLiabilities(request)
      .then(response => {
        FileSaver.saveAs(response, Utils.GetExcelName('Balance Pymes - Patrimonio Neto y Pasivo'));
        this.isLoadingEquityAndLiabilities = false;
      })
      .catch(() => {
        this.displayInfo('Ha ocurrido un error inesperado al exportar el documento excel');
        this.isLoadingEquityAndLiabilities = false;
      });
  }

  public pdfBalanceSheetEquityAndLiabilities(): void {
    if (!Utils.IsTierFour()) {
      this.displayInfo(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE);
      return;
    }

    this.isLoadingEquityAndLiabilities = true;

    const request = {
      global: this.globalFilterBalance.value,
      from: this.fromFilterBalance.value,
      to: this.toFilterBalance.value,
      balanceSheetEquityAndLiabilitiesViewModel: this.accountingBalanceSheetEquityAndLiabilitiesModel
    };

    this.accountingReportsService
      .getPdfBalanceSheetEquityAndLiabilities(request)
      .then(response => {
        FileSaver.saveAs(response, Utils.GetPdfName('Balance Pymes - Patrimonio Neto y Pasivo'));
        this.isLoadingEquityAndLiabilities = false;
      })
      .catch(() => {
        this.displayInfo('Ha ocurrido un error inesperado al exportar el documento PDF');
        this.isLoadingEquityAndLiabilities = false;
      });
  }

  private displayInfo(message: string): void {
    const options = new MatSnackBarConfig();
    options.duration = 10000;
    options.panelClass = ['custom-snackbar', 'custom-snackbar-container'];
    this.snackBar.open(message, '', options);
  }
  public trackByIndex(i: number) { return i; }

}